/**
 * PORTED — RO-3-00c / #44470.
 * Origin: `excellent/app/_modes/lane-automations.tsx`.
 *
 * Changes:
 *   `attachLoopToLane` / `detachLoopFromLane` server actions
 *     → `apiCall(pulse.laneAttach|laneDetach)` (RO-2-11 mounts)
 *   `setLoopStatusAction` → `apiCall` against agent-workflows setStatus when
 *     available; status toggle uses the existing workflows contract if present,
 *     otherwise defers (attach/detach still work).
 *   `useRouter` from next → `useNavigate` from `@excellent/ui/router`
 */

import { useState, useTransition } from "react";
import { Card, CardContent, Button, EmptyState, IntentBadge, Input, cn } from "@excellent/ui";
import { Play, Pause, Plus, X, Workflow } from "@excellent/ui/icons";
import { apiCall } from "@excellent/ui/api";
import { pulse } from "@excellent/ui/api/contracts/pulse";
import { useNavigate } from "@excellent/ui/router";

export type LaneLoopRow = {
  id: number;
  uid: string | null;
  slug: string;
  title: string;
  description: string | null;
  status: string;
  domain: string | null;
  lane_uid: string | null;
};

const STATUS_INTENT: Record<string, "success" | "warning" | "neutral"> = {
  active: "success",
  paused: "warning",
  draft: "neutral",
};

export function LaneAutomations({
  laneUid,
  laneLabel,
  lanePath,
  bound,
  attachable,
}: {
  laneUid: string;
  laneLabel: string;
  lanePath: string;
  bound: LaneLoopRow[];
  attachable: LaneLoopRow[];
}) {
  const navigate = useNavigate();
  const [pending, start] = useTransition();
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");

  const refresh = () => {
    // Prefer invalidate over a same-path push — re-run loaders / SWR keys.
    navigate.refresh();
  };

  const run = (fn: () => Promise<unknown>) =>
    start(async () => {
      await fn();
      refresh();
    });

  const q = query.trim().toLowerCase();
  const candidates = q
    ? attachable.filter(
        (l) => l.title.toLowerCase().includes(q) || l.slug.toLowerCase().includes(q),
      )
    : attachable;

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="px-0">
        <header className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
          <p className="min-w-0 px-1 type-subtext text-muted-foreground">
            Binding organizes automations by lifecycle stage — it doesn&rsquo;t change how or when a
            workflow runs.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            aria-expanded={picking}
            onClick={() => setPicking((p) => !p)}
          >
            {picking ? <X className="size-3.5" /> : <Plus className="size-3.5" />}
            {picking ? "Done" : "Attach a workflow"}
          </Button>
        </header>

        {picking ? (
          <div className="flex flex-col gap-2 border-b border-border bg-foreground/[0.015] px-3 py-3">
            <Input
              placeholder="Search workflows…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              size="sm"
            />
            {candidates.length === 0 ? (
              <p className="px-1 type-subtext text-muted-foreground">No unbound workflows.</p>
            ) : (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                {candidates.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      disabled={pending}
                      className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left type-body hover:bg-muted"
                      onClick={() =>
                        run(() =>
                          apiCall(pulse.laneAttach, {
                            body: { loopSlug: l.slug, laneUid },
                          }),
                        )
                      }
                    >
                      <span className="truncate font-medium">{l.title}</span>
                      <span className="shrink-0 type-subtext text-muted-foreground">{l.slug}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {bound.length === 0 ? (
          <EmptyState
            variant="inline"
            icon={<Workflow className="size-5" />}
            title={`No automations on ${laneLabel}`}
            description="Attach a workflow to organize it under this lifecycle step."
          />
        ) : (
          <ul className="divide-y divide-border">
            {bound.map((l) => (
              <li
                key={l.id}
                className={cn(
                  "flex items-center justify-between gap-3 px-3 py-2.5",
                  pending && "opacity-60",
                )}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate type-body font-medium">{l.title}</span>
                    <IntentBadge
                      intent={STATUS_INTENT[l.status] ?? "neutral"}
                      className="type-subtext"
                    >
                      {l.status}
                    </IntentBadge>
                  </div>
                  {l.description ? (
                    <p className="truncate type-subtext text-muted-foreground">{l.description}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={pending}
                    aria-label={l.status === "active" ? "Pause" : "Activate"}
                    title={
                      l.status === "active"
                        ? "Pause (use the workflow page — status toggle is on agent-workflows)"
                        : "Activate (use the workflow page)"
                    }
                  >
                    {l.status === "active" ? (
                      <Pause className="size-3.5" />
                    ) : (
                      <Play className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={pending}
                    onClick={() =>
                      run(() => apiCall(pulse.laneDetach, { body: { loopSlug: l.slug } }))
                    }
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
